import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import PropTypes from 'prop-types';
import GlobalLoadingIndicator from '../ui/components/common/GlobalLoadingIndicator';

const RouteChangeContext = createContext({
  isChangingRoute: false,
  setRouteChangeComplete: () => {},
  pageReady: () => {},
});

export const useRouteChange = () => useContext(RouteChangeContext);

export const RouteChangeProvider = ({ 
  children, 
  transitionTimeout = 2000,  // Longer max timeout
  showDelay = 400,           // Increased delay before showing indicator
  minIndicatorDisplay = 500, // Minimum time to show indicator once visible
}) => {
  const [isChangingRoute, setIsChangingRoute] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const timerRef = useRef(null);
  const delayTimerRef = useRef(null);
  const minDisplayTimerRef = useRef(null);
  const loadStartTimeRef = useRef(0);
  const indicatorShowTimeRef = useRef(0);
  const location = useLocation();
  const navigationType = useNavigationType();

  // Start transition when location changes (except on initial load)
  useEffect(() => {
    // Skip the initial render
    if (navigationType === 'POP' && timerRef.current === null) return;
    
    // Clear any existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    if (minDisplayTimerRef.current) clearTimeout(minDisplayTimerRef.current);
    
    // Record start time for performance tracking
    loadStartTimeRef.current = performance.now();
    
    // Set changing route state immediately
    setIsChangingRoute(true);
    
    // Only show loading indicator if transition takes longer than showDelay
    delayTimerRef.current = setTimeout(() => {
      // Check if we're still in a loading state
      const timeElapsed = performance.now() - loadStartTimeRef.current;
      
      // If the page has loaded very quickly (under 100ms), don't show indicator at all
      if (timeElapsed < 100) {
        setRouteChangeComplete();
        return;
      }
      
      setShowLoading(true);
      indicatorShowTimeRef.current = performance.now();
    }, showDelay);
    
    // Safety timeout to clear loading state after maximum time
    timerRef.current = setTimeout(() => {
      setRouteChangeComplete();
    }, transitionTimeout);
    
    // Clean up timers on unmount
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
      if (minDisplayTimerRef.current) clearTimeout(minDisplayTimerRef.current);
    };
  }, [location, transitionTimeout, showDelay]);

  // Called when page content has finished loading
  const pageReady = () => {
    // If indicator hasn't shown yet, cancel it
    if (!showLoading) {
      setRouteChangeComplete();
      return;
    }
    
    // If indicator is showing, ensure it displays for minimum time
    const indicatorDisplayTime = performance.now() - indicatorShowTimeRef.current;
    
    if (indicatorDisplayTime < minIndicatorDisplay) {
      const remainingTime = minIndicatorDisplay - indicatorDisplayTime;
      
      minDisplayTimerRef.current = setTimeout(() => {
        setRouteChangeComplete();
      }, remainingTime);
    } else {
      // Indicator has been shown long enough, hide it now
      setRouteChangeComplete();
    }
  };

  const setRouteChangeComplete = () => {
    // Clear timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    if (minDisplayTimerRef.current) clearTimeout(minDisplayTimerRef.current);
    
    // Reset states
    setIsChangingRoute(false);
    setShowLoading(false);
  };

  return (
    <RouteChangeContext.Provider value={{ 
      isChangingRoute, 
      setRouteChangeComplete,
      pageReady
    }}>
      <GlobalLoadingIndicator
        isVisible={showLoading} 
        message="Loading page..."
        fullscreen={false}
        position="top"
      />
      {children}
    </RouteChangeContext.Provider>
  );
};

RouteChangeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  transitionTimeout: PropTypes.number,
  showDelay: PropTypes.number,
  minIndicatorDisplay: PropTypes.number,
};

export default RouteChangeContext; 